/* ==========================================================================
   CsvCatalogUpload Component
   Task F39: CSV file upload for bulk catalog import
   ========================================================================== */

import { useState, useRef, useCallback } from 'react'
import {
  Download,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  Info,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import type { CsvImportError, CsvImportResult } from '@/api/types'

/**
 * One CSV error with everything needed to find it in the spreadsheet:
 * the exact cell (matching Excel/Sheets numbering), the product on that
 * row, the offending column, and the rejected value.
 */
function ErrorRow({ err }: { err: CsvImportError }) {
  const isRowError = err.field !== 'file' && err.field !== 'headers'
  return (
    <div className="px-3 py-2 text-sm">
      <span className="font-medium text-red-700">
        {isRowError ? `Row ${err.row}` : 'File'}
        {err.cell && (
          <span className="text-red-600"> · cell {err.cell}</span>
        )}
      </span>
      {err.product_code && (
        <>
          {' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
            {err.product_code}
          </code>
        </>
      )}
      {err.display_name && (
        <span className="text-charcoal/60"> {err.display_name}</span>
      )}
      {isRowError && err.field !== 'data' && (
        <span className="text-red-500"> ({err.field})</span>
      )}
      <span className="text-charcoal/70">: {err.message}</span>
      {err.value != null && err.value !== '' && (
        <span className="text-charcoal/50">
          {' '}
          — got &ldquo;{err.value}&rdquo;
        </span>
      )}
    </div>
  )
}

interface CsvCatalogUploadProps {
  onImport: (file: File) => Promise<CsvImportResult>
  /**
   * When provided, uploads run a dry-run preview first: the user sees what
   * would change and must press "Apply Changes" before onImport runs
   * (PORTAL-58 edit mode).
   */
  onPreview?: (file: File) => Promise<CsvImportResult>
  onDownloadTemplate: () => Promise<Blob>
  onSuccess?: () => void
}

type UploadState = 'idle' | 'uploading' | 'preview' | 'applying' | 'results'

const MAX_SIZE_MB = 5

export function CsvCatalogUpload({
  onImport,
  onPreview,
  onDownloadTemplate,
  onSuccess,
}: CsvCatalogUploadProps) {
  const [state, setState] = useState<UploadState>('idle')
  const [result, setResult] = useState<CsvImportResult | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [errorsExpanded, setErrorsExpanded] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true)
    try {
      const blob = await onDownloadTemplate()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'catalog_template.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download template')
    } finally {
      setDownloadingTemplate(false)
    }
  }, [onDownloadTemplate])

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)
      setResult(null)

      // Client-side validations
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a .csv file')
        return
      }

      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`File is too large. Maximum size is ${MAX_SIZE_MB}MB.`)
        return
      }

      setState('uploading')
      try {
        if (onPreview) {
          const previewResult = await onPreview(file)
          setResult(previewResult)
          setPendingFile(file)
          setState('preview')
        } else {
          const importResult = await onImport(file)
          setResult(importResult)
          setState('results')
          if (importResult.inserted > 0 || importResult.updated > 0) {
            onSuccess?.()
          }
        }
      } catch (err: unknown) {
        setState('idle')
        if (err instanceof Error) {
          setError(err.message)
        } else {
          setError('Import failed. Please try again.')
        }
      }
    },
    [onImport, onPreview, onSuccess]
  )

  const handleApply = useCallback(async () => {
    if (!pendingFile) return
    setError(null)
    setState('applying')
    try {
      const importResult = await onImport(pendingFile)
      setResult(importResult)
      setPendingFile(null)
      setState('results')
      if (importResult.inserted > 0 || importResult.updated > 0) {
        onSuccess?.()
      }
    } catch (err: unknown) {
      setState('preview')
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Import failed. Please try again.')
      }
    }
  }, [onImport, onSuccess, pendingFile])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleReset = () => {
    setState('idle')
    setResult(null)
    setPendingFile(null)
    setError(null)
    setErrorsExpanded(false)
  }

  // Preview state — dry-run summary + explicit confirm (PORTAL-58)
  if ((state === 'preview' || state === 'applying') && result) {
    const hasChanges = result.inserted > 0 || result.updated > 0
    const clearedFieldCount = result.to_update.reduce(
      (total, change) => total + change.changes.filter((fc) => !fc.new).length,
      0
    )
    const clearedProductCount = result.to_update.filter((change) =>
      change.changes.some((fc) => !fc.new)
    ).length
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-soft-lavender/50 border border-lavender rounded-md">
          <Info className="h-5 w-5 text-deep-purple flex-shrink-0" />
          <p className="text-sm text-charcoal">
            Preview — nothing has been changed yet.{' '}
            {hasChanges
              ? 'Review the changes below, then press Apply Changes.'
              : 'This file matches the current catalog; there is nothing to apply.'}
          </p>
        </div>

        {clearedFieldCount > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-md">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">
                {clearedFieldCount} field{clearedFieldCount !== 1 ? 's' : ''} across{' '}
                {clearedProductCount} product{clearedProductCount !== 1 ? 's' : ''} will be
                cleared.
              </span>{' '}
              Empty cells in the file erase the current value on the matched
              product — check the &ldquo;will be cleared&rdquo; entries below
              before applying.
            </p>
          </div>
        )}

        {result.to_update.length > 0 && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-charcoal border-b border-gray-200">
              {result.to_update.length} product{result.to_update.length !== 1 ? 's' : ''} will be updated
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {result.to_update.map((change) => (
                <div key={change.product_code} className="px-3 py-2 text-sm">
                  <div>
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                      {change.product_code}
                    </code>{' '}
                    <span className="text-charcoal font-medium">
                      {change.display_name}
                    </span>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {change.changes.map((fieldChange) => (
                      <div
                        key={fieldChange.field}
                        className="pl-2 text-xs break-words"
                      >
                        <span className="text-gray-500 capitalize">
                          {fieldChange.field.replace(/_/g, ' ')}:
                        </span>{' '}
                        <span className="text-red-600 line-through">
                          {fieldChange.old || '(empty)'}
                        </span>
                        <span className="text-gray-400"> → </span>
                        {fieldChange.new ? (
                          <span className="text-green-700">{fieldChange.new}</span>
                        ) : (
                          <span className="inline-flex items-center bg-amber-100 text-amber-900 font-medium px-1.5 py-0.5 rounded">
                            will be cleared
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.to_create.length > 0 && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-charcoal border-b border-gray-200">
              {result.to_create.length} new product{result.to_create.length !== 1 ? 's' : ''} will be created
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
              {result.to_create.map((change) => (
                <div key={change.product_code} className="px-3 py-2 text-sm">
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                    {change.product_code}
                  </code>{' '}
                  <span className="text-charcoal">{change.display_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.new_categories.length > 0 && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-charcoal border-b border-gray-200">
              {result.new_categories.length} categor{result.new_categories.length !== 1 ? 'ies' : 'y'} will be created
            </div>
            <div className="px-3 py-2 flex flex-wrap gap-1.5">
              {result.new_categories.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center bg-soft-lavender text-deep-purple text-sm px-2 py-0.5 rounded"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        {result.unchanged > 0 && (
          <p className="text-xs text-gray-500">
            {result.unchanged} product{result.unchanged !== 1 ? 's' : ''} in the
            file {result.unchanged !== 1 ? 'are' : 'is'} identical to the catalog
            and will be skipped.
          </p>
        )}

        {/* Errors list (rows that will be skipped) */}
        {result.errors.length > 0 && (
          <div className="border border-red-200 rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setErrorsExpanded(!errorsExpanded)}
              className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors text-left"
            >
              <span className="text-sm font-medium text-red-800">
                {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} with errors will be skipped
              </span>
              {errorsExpanded ? (
                <ChevronUp className="h-4 w-4 text-red-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-red-600" />
              )}
            </button>
            {errorsExpanded && (
              <div className="max-h-60 overflow-y-auto divide-y divide-red-100">
                {result.errors.map((err, i) => (
                  <ErrorRow key={i} err={err} />
                ))}
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-error flex items-center gap-1" role="alert">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReset}
            disabled={state === 'applying'}
          >
            Cancel
          </Button>
          {hasChanges && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleApply}
              loading={state === 'applying'}
            >
              Apply Changes
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Results state
  if (state === 'results' && result) {
    return (
      <div className="space-y-3">
        {/* Success banner */}
        {(result.inserted > 0 || result.updated > 0) && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">
              {[
                result.inserted > 0
                  ? `${result.inserted} product${result.inserted !== 1 ? 's' : ''} imported`
                  : null,
                result.updated > 0
                  ? `${result.updated} product${result.updated !== 1 ? 's' : ''} updated`
                  : null,
              ]
                .filter(Boolean)
                .join(', ')}{' '}
              successfully
            </p>
          </div>
        )}

        {/* No products imported */}
        {result.inserted === 0 && result.updated === 0 && result.errors.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              No products were imported. Please fix the errors below and try again.
            </p>
          </div>
        )}

        {/* Errors list */}
        {result.errors.length > 0 && (
          <div className="border border-red-200 rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setErrorsExpanded(!errorsExpanded)}
              className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 transition-colors text-left"
            >
              <span className="text-sm font-medium text-red-800">
                {result.errors.length} error{result.errors.length !== 1 ? 's' : ''} found
              </span>
              {errorsExpanded ? (
                <ChevronUp className="h-4 w-4 text-red-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-red-600" />
              )}
            </button>
            {errorsExpanded && (
              <div className="max-h-60 overflow-y-auto divide-y divide-red-100">
                {result.errors.map((err, i) => (
                  <ErrorRow key={i} err={err} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload another */}
        <Button variant="secondary" size="sm" onClick={handleReset}>
          Upload Another CSV
        </Button>
      </div>
    )
  }

  // Idle / uploading state
  return (
    <div className="space-y-3">
      {/* Template download + instructions */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="inline-flex items-center gap-1.5 text-sm text-deep-purple hover:underline disabled:opacity-50"
          >
            {downloadingTemplate ? (
              <Spinner size="sm" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download CSV Template
          </button>
          <span className="text-xs text-gray-400">Includes 2 example rows</span>
        </div>

        <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 space-y-1">
          <p className="flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <span>
              Columns marked with <span className="font-semibold">*</span> in the template are required.
              All other columns are optional.
            </span>
          </p>
          <p className="flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <span>
              To add product notes, add columns named{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">note:Label</code>{' '}
              (e.g. <code className="bg-gray-200 px-1 rounded text-[11px]">note:Cost</code>).
              The cell value becomes the note&apos;s value, visible read-only to providers.
            </span>
          </p>
          <p className="flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <span>
              To add custom prescription fields, use columns named{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">field:&lt;type&gt;:Label</code>{' '}
              (e.g. <code className="bg-gray-200 px-1 rounded text-[11px]">field:dropdown:Strength</code>).
              Valid types: <code className="bg-gray-200 px-1 rounded text-[11px]">text</code>,{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">number</code>,{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">date</code>,{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">dropdown</code>,{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">checkbox</code>. Append{' '}
              <code className="bg-gray-200 px-1 rounded text-[11px]">:required</code> to make
              the field required. For dropdowns, set the cell to pipe-separated options
              (<code className="bg-gray-200 px-1 rounded text-[11px]">A|B|C</code>). Max 20
              field columns per file. See the user guide for full details.
            </span>
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-md p-6
          transition-colors cursor-pointer
          ${dragOver ? 'border-deep-purple bg-soft-lavender/30' : 'border-gray-300 hover:border-gray-400'}
          ${error ? 'border-error' : ''}
          ${state === 'uploading' ? 'pointer-events-none opacity-60' : ''}
        `}
        onClick={() => state !== 'uploading' && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          className="hidden"
          aria-label="Upload CSV file"
        />

        <div className="flex flex-col items-center gap-2 text-center">
          {state === 'uploading' ? (
            <>
              <Spinner size="md" />
              <p className="text-sm text-gray-500">Importing products...</p>
            </>
          ) : (
            <>
              <FileSpreadsheet className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                <span className="text-deep-purple font-medium">Click to upload</span>
                {' '}or drag and drop a CSV file
              </p>
              <p className="text-xs text-gray-500">
                .csv only, max {MAX_SIZE_MB}MB
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-error flex items-center gap-1" role="alert">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}
